import { setOpenAskAi } from '@modules/editor/openAskAiUtils';
import {
  InputBase,
  InputBaseProps,
  Stack,
  Tooltip,
  styled,
} from '@mui/material';
import { Editor } from '@tiptap/react';
import React, { forwardRef, useState } from 'react';
import { IEmoji } from 'src/@types/chat';
import { ConversationMessageContentType } from 'src/@types/conversation';
import { AudioRecorder } from 'src/components/AudioRecorder';
import { EmojiPopover } from 'src/components/EmojiPickerPopover/EmojiPopover';
import { PopoverTrigger } from 'src/components/PopoverTrigger';
import { ScreenRecord } from 'src/components/ScreenRecorder/ScreenRecorder';
import Iconify from 'src/components/iconify';
import { SoundBoardPopover } from 'src/features/RTCBar/GatheringSpacesSoundBoard/SoundBoardPopover';
import { useLocales } from 'src/locales';
import { SoundBoardData } from 'src/requests/soundboard';
import { useConvertMediaFile } from '../../conversation/hooks/useConvertMediaFile';
import { ConversationMessageEditor } from '../ConversationMessageEditor';
import { ConversationReplyToMessage } from './ConversationReplyToMessage';
import { MediaInputActions } from './MediaInputActions';
import { StyledInputButton } from './styles';
const InputComponent = forwardRef((props: any, ref) => (
  <ConversationMessageEditor {...props} ref={ref} />
));

interface Props extends InputBaseProps {
  // TODO: rename to onSendMessage
  handleTextMessage(message: string): void;
  // TODO: rename to onFileSelect
  handleFileSelect(type: string, file: File): void;
  onSendSoundBoard?: (sound: Partial<SoundBoardData>) => void;

  editor: Editor;
  className?: string;
  iconSize?: number;
  showMediaInputActions?: boolean;

  // TODO: all those should be false by default to use shorthand props value
  allowAudio?: boolean;
  allowEmoji?: boolean;
  allowAskAi?: boolean;
  allowOptions?: boolean;
  allowScreenRecord?: boolean;
  allowSoundBoard?: boolean;

  //? this just exist for now because ConversationReplyToMessage use a context that SHOULD NOT BE HERE
  allowReply?: boolean;
}

export const ConversationMessageInput = ({
  disabled,
  handleFileSelect,
  handleTextMessage,
  onSendSoundBoard,
  editor,
  iconSize = 24,
  showMediaInputActions,
  sx,
  className,
  allowAudio = true,
  allowEmoji = true,
  allowReply = true,
  allowAskAi = true,
  allowOptions = true,
  allowScreenRecord = true,
  allowSoundBoard = true,
  ...other
}: Props) => {
  const [isRecording, setIsRecording] = useState(false);
  const { translate } = useLocales();

  const { checkFileToConvert } = useConvertMediaFile();

  const onSend = () => {
    const isEmpty = !editor || !editor.getText().replace(/\s/g, '');
    if (disabled || !editor || isEmpty || editor.isEmpty) return;

    const message = editor.getHTML();
    if (!message) return;
    handleTextMessage(message);

    editor.commands.setContent('');
  };

  const handleSend = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled || e.key !== 'Enter' || e.shiftKey) return;
    onSend();
  };

  const onSendAudio = (file: File) => {
    if (disabled) return;
    handleFileSelect(ConversationMessageContentType.AUDIO, file);
  };

  const handleSelectEmoji = (item: IEmoji) => {
    if (disabled) return;
    const rangeFrom = editor?.state.selection.from;
    editor.chain().addEmoji({ item, rangeFrom }).focus(rangeFrom).run();
  };

  const handleAskAi = () => {
    editor.chain().focus().selectAll().run();
    setOpenAskAi(editor.view, {
      open: true,
    });
  };

  const startRecording = () => {
    setIsRecording(true);
  };

  const stopRecording = () => {
    setIsRecording(false);
  };

  const onSendAudioHandler = (audio: File) => {
    onSendAudio(audio);
    stopRecording();
  };

  const handleOptions = () => {
    editor.commands.insertContent(' /');
  };

  const handleConvertFile = async (
    type: ConversationMessageContentType,
    file: File,
  ) => {
    const mediaFile = await checkFileToConvert(type, file);
    handleFileSelect(type, mediaFile);
  };

  const handleSendScreenRecord = (blob: Blob | null) => {
    const lastModified = new Date().getTime();
    if (!blob) return;
    const file = new File([blob], `recorded-video_${lastModified}.mp4`, {
      type: 'video/mp4',
      lastModified,
    });
    handleFileSelect(ConversationMessageContentType.VIDEO, file);
  };

  return (
    <>
      {allowReply && <ConversationReplyToMessage editor={editor} />}
      <Stack flexGrow={1} gap={1} className={className}>
        <InputBase
          disabled={isRecording || disabled}
          inputComponent={InputComponent}
          inputProps={{ editor }}
          onKeyDown={handleSend}
          fullWidth
          sx={{
            fontSize: '.875rem',
            ...sx,
          }}
          {...other}
          onClick={() => {
            !disabled && editor.view.focus();
          }}
        />

        {isRecording ? (
          <Stack direction="row" py={1} my={0.125} flexGrow={1}>
            <AudioRecorder
              onStop={stopRecording}
              onSend={onSendAudioHandler}
              autoRecord
            />
          </Stack>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            py={1}
            gap={1.5}
            flexGrow={1}
          >
            {allowAskAi && (
              <Tooltip title={translate('shared.input.ask_to_copera')} arrow>
                <StyledInputButton
                  onClick={handleAskAi}
                  disabled={disabled}
                  sx={{
                    width: iconSize,
                    height: iconSize,
                  }}
                >
                  <Iconify icon="copera:ai" />
                </StyledInputButton>
              </Tooltip>
            )}
            {showMediaInputActions && (
              <MediaInputActions
                disabled={disabled}
                onSelectFile={handleConvertFile}
                iconSize={iconSize}
              />
            )}
            {allowEmoji && (
              <EmojiPopover
                onSelectEmoji={(emoji) => handleSelectEmoji(emoji)}
                disabled={disabled}
                sx={{
                  width: iconSize,
                  height: iconSize,
                  '&:hover': {
                    color: (theme) => theme.palette.text.primary,
                  },
                  '&.MuiIconButton-root': {
                    backgroundColor: 'transparent',
                    padding: 0,
                  },
                }}
              />
            )}
            {allowOptions && (
              <Tooltip
                title={translate('shared.input.open_input_options')}
                arrow
              >
                <StyledInputButton
                  onClick={handleOptions}
                  disabled={disabled}
                  sx={{
                    width: iconSize,
                    height: iconSize,
                  }}
                >
                  <Iconify icon="squared-slash" />
                </StyledInputButton>
              </Tooltip>
            )}
            {allowScreenRecord && (
              <StyledScreenRecord
                uploadRecordInstantly={handleSendScreenRecord}
                disabled={disabled}
              />
            )}

            {allowSoundBoard && (
              <PopoverTrigger
                popover={SoundBoardPopover}
                context={{ onSelectSound: onSendSoundBoard! }}
              >
                <Tooltip
                  title={translate('communicate.sound_board.title')}
                  arrow
                >
                  <StyledInputButton
                    disabled={disabled}
                    sx={{
                      width: iconSize,
                      height: iconSize,
                    }}
                  >
                    <Iconify icon="tabler:speakerphone" />
                  </StyledInputButton>
                </Tooltip>
              </PopoverTrigger>
            )}

            <Stack flexGrow={1} mx={1}></Stack>
            {allowAudio && editor.isEmpty ? (
              <Tooltip title={translate('shared.input.record_audio')} arrow>
                <StyledInputButton disabled={disabled} onClick={startRecording}>
                  <Iconify icon="tabler:microphone" />
                </StyledInputButton>
              </Tooltip>
            ) : (
              <Tooltip title={translate('shared.input.send')} arrow>
                <StyledInputButton
                  disabled={disabled}
                  onClick={onSend}
                  sx={{
                    width: iconSize,
                    height: iconSize,
                  }}
                >
                  <Iconify icon="tabler:send-2" />
                </StyledInputButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
    </>
  );
};

const StyledScreenRecord = styled(ScreenRecord)(({ theme }) => ({
  width: 24,
  height: 24,
  color: theme.palette.action.active,
  '&:hover': {
    color: theme.palette.text.primary,
  },
  '&.MuiIconButton-root': {
    backgroundColor: 'transparent',
    padding: 0,
  },
}));
